/* eslint-disable no-unused-vars */
import { useState, useRef } from "react";
import { Upload, Download, X, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";

// All valid field keys that map to FinancialCalculator inputs
const FIELD_KEYS = [
  "mrr", "growth", "nrr", "grr", "grossMargin", "expansionRevenue",
  "burn", "salesSpend", "rdSpend", "employees", "cash",
  "cac", "ltv", "churn", "revenueChurn", "acv",
  "dauMau", "activationRate", "featureAdoption", "arrPerSeat",
  "ebitdaMargin", "operatingLeverage", "contributionMargin", "workingCapital",
];

// Smart column name detection — maps common Excel column names to field keys
const SMART_MAP = {
  // MRR
  "mrr": "mrr", "monthly recurring revenue": "mrr", "monthly revenue": "mrr",
  "recurring revenue": "mrr",
  // Growth
  "growth": "growth", "mom growth": "growth", "growth rate": "growth",
  "month over month growth": "growth", "monthly growth": "growth",
  // NRR
  "nrr": "nrr", "net revenue retention": "nrr", "net retention": "nrr",
  // GRR
  "grr": "grr", "gross revenue retention": "grr", "gross retention": "grr",
  // Gross margin
  "gross margin": "grossMargin", "margin": "grossMargin", "gm": "grossMargin",
  // Burn
  "burn": "burn", "burn rate": "burn", "monthly burn": "burn",
  "net burn": "burn", "cash burn": "burn",
  // Sales spend
  "sales spend": "salesSpend", "s&m": "salesSpend", "marketing spend": "salesSpend",
  "sales and marketing": "salesSpend", "sales & marketing": "salesSpend",
  // Employees
  "employees": "employees", "headcount": "employees", "team size": "employees",
  "staff": "employees", "fte": "employees",
  // Cash
  "cash": "cash", "cash in bank": "cash", "cash balance": "cash",
  "bank balance": "cash", "runway cash": "cash",
  // CAC
  "cac": "cac", "customer acquisition cost": "cac", "acquisition cost": "cac",
  // LTV
  "ltv": "ltv", "lifetime value": "ltv", "customer lifetime value": "ltv",
  "clv": "ltv",
  // Churn
  "churn": "churn", "churn rate": "churn", "logo churn": "churn",
  "customer churn": "churn", "monthly churn": "churn",
  // ACV
  "acv": "acv", "average contract value": "acv", "contract value": "acv",
};

function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Try to find the right sheet — prefer "Binos Metrics" sheet
        let sheetName = workbook.SheetNames.find(n =>
          n.toLowerCase().includes("binos") || n.toLowerCase().includes("metric")
        ) || workbook.SheetNames[0];

        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });

        const result = {};
        let mapped = 0;
// eslint-disable-next-line no-unused-vars
        let smartMapped = 0;

        // Method 1: Template format — look for field keys in column B, values in column C
        for (const row of rows) {
          if (!row || row.length < 3) continue;
          const key = String(row[1] || "").trim().toLowerCase();
          const val = row[2];
          if (FIELD_KEYS.includes(key) && val !== null && val !== "" && !isNaN(parseFloat(val))) {
            result[key] = parseFloat(val);
            mapped++;
          }
        }

        // Method 2: Smart detection — look for recognizable column headers
        if (mapped < 3) {
          // Find header row
          let headerRow = -1;
          let headerData = [];
          for (let i = 0; i < Math.min(10, rows.length); i++) {
            const row = rows[i];
            if (!row) continue;
            const rowStr = row.map(c => String(c || "").toLowerCase());
            const matches = rowStr.filter(c => Object.keys(SMART_MAP).some(k => c.includes(k)));
            if (matches.length >= 2) {
              headerRow = i;
              headerData = rowStr;
              break;
            }
          }

          if (headerRow >= 0) {
            // Map each column header to a field key
            const colMap = {};
            headerData.forEach((header, colIdx) => {
              const h = header.trim();
              for (const [pattern, fieldKey] of Object.entries(SMART_MAP)) {
                if (h.includes(pattern) && !colMap[colIdx]) {
                  colMap[colIdx] = fieldKey;
                }
              }
            });

            // Read data rows
            for (let i = headerRow + 1; i < rows.length; i++) {
              const row = rows[i];
              if (!row) continue;
              for (const [colIdx, fieldKey] of Object.entries(colMap)) {
                const val = row[colIdx];
                if (val !== null && val !== "" && !isNaN(parseFloat(val)) && !result[fieldKey]) {
                  result[fieldKey] = parseFloat(val);
                  smartMapped++;
                }
              }
              if (Object.keys(result).length >= 5) break;
            }
          }

          // Method 3: Key-value pairs — look for metric name + number pairs in any column
          if (Object.keys(result).length < 3) {
            for (const row of rows) {
              if (!row) continue;
              for (let i = 0; i < row.length - 1; i++) {
                const cellText = String(row[i] || "").toLowerCase().trim();
                const nextVal = row[i + 1];
                if (nextVal !== null && !isNaN(parseFloat(nextVal))) {
                  for (const [pattern, fieldKey] of Object.entries(SMART_MAP)) {
                    if (cellText.includes(pattern) && !result[fieldKey]) {
                      result[fieldKey] = parseFloat(nextVal);
                      smartMapped++;
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        resolve({
          values: result,
          count: Object.keys(result).length,
          method: mapped > 0 ? "template" : "smart",
          sheetName,
        });
      } catch (err) {
        reject(new Error("Could not read Excel file. Make sure it's a valid .xlsx file."));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}

export default function ExcelImport({ onImport, onDownloadTemplate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError("Please upload an Excel file (.xlsx, .xls) or CSV file.");
      return;
    }
    setIsProcessing(true);
    setError("");
    setResult(null);
    try {
      const parsed = await parseExcel(file);
      if (parsed.count === 0) {
        setError("No recognisable metrics found. Try downloading and using the Binos template.");
        setIsProcessing(false);
        return;
      }
      setResult(parsed);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleConfirm = () => {
    if (result?.values) {
      onImport(result.values);
      setIsOpen(false);
      setResult(null);
    }
  };

  const reset = () => { setResult(null); setError(""); };

  if (!isOpen) {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setIsOpen(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 9, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.6)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#dc2626"; e.currentTarget.style.color = "#dc2626"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}>
          <FileSpreadsheet style={{ width: 14, height: 14 }} />
          Import from Excel
        </button>
        <button onClick={onDownloadTemplate}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}>
          <Download style={{ width: 14, height: 14 }} />
          Download Template
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, width: "100%", maxWidth: 480, overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>Import from Excel</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>Upload your metrics file or use the Binos template</p>
          </div>
          <button onClick={() => { setIsOpen(false); reset(); }}
            style={{ padding: 7, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>

          {/* Download template CTA */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 10, marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>Using Binos for the first time?</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>Download our template and fill it in</p>
            </div>
            <button onClick={onDownloadTemplate}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "#dc2626", border: "none", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              <Download style={{ width: 12, height: 12 }} /> Get Template
            </button>
          </div>

          {/* Drop zone */}
          {!result && (
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${isDragging ? "#dc2626" : "rgba(255,255,255,0.12)"}`, borderRadius: 12, padding: "36px 24px", textAlign: "center", cursor: "pointer", transition: "all 0.2s", background: isDragging ? "rgba(220,38,38,0.04)" : "transparent" }}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
              {isProcessing ? (
                <div>
                  <Loader2 style={{ width: 32, height: 32, color: "#dc2626", margin: "0 auto 12px" }} className="animate-spin" />
                  <p style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", margin: 0 }}>Reading your file...</p>
                </div>
              ) : (
                <div>
                  <Upload style={{ width: 32, height: 32, color: "rgba(255,255,255,0.2)", margin: "0 auto 12px" }} />
                  <p style={{ fontSize: 14, fontWeight: 700, color: "#fff", margin: "0 0 4px" }}>Drop your Excel file here</p>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", margin: 0 }}>.xlsx, .xls, or .csv · Click to browse</p>
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 10, marginTop: 12 }}>
              <AlertCircle style={{ width: 15, height: 15, color: "#dc2626", flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: "#dc2626", margin: 0, fontWeight: 500 }}>{error}</p>
            </div>
          )}

          {/* Results preview */}
          {result && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 10, marginBottom: 14 }}>
                <CheckCircle2 style={{ width: 16, height: 16, color: "#16a34a", flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", margin: "0 0 2px" }}>
                    {result.count} metric{result.count !== 1 ? "s" : ""} found
                  </p>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>
                    Sheet: {result.sheetName} · {result.method === "template" ? "Binos template detected" : "Smart detection used"}
                  </p>
                </div>
              </div>

              {/* Preview of mapped values */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "8px 14px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Values to import</p>
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {Object.entries(result.values).map(([key, val]) => (
                    <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{key}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{val.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={reset}
                  style={{ flex: 1, padding: "11px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}>
                  Upload Different File
                </button>
                <button onClick={handleConfirm}
                  style={{ flex: 1, padding: "11px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <CheckCircle2 style={{ width: 14, height: 14 }} /> Import {result.count} Metrics
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
